'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A short identifier shown inline with one-click copy.
 *
 * These ids are the values operators paste into Basis Theory, into scripts, and
 * into support threads — and the ones they compare against what a checkout page
 * actually ships. Reading them off the screen and retyping is where
 * transcription errors come from, so the copy is the point.
 */
export function CopyableValue({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  const { t } = useTranslation('manageApplePay');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? t('domains.copied') : `${label}: ${value}`}
      className={cn(
        'text-muted-foreground hover:text-foreground group inline-flex max-w-full items-center gap-1.5 text-xs transition-colors',
        className,
      )}
    >
      <span className="font-mono break-all">{value}</span>
      {copied ? (
        <Check className="h-3 w-3 shrink-0 text-emerald-600" />
      ) : (
        <Copy className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
      )}
    </button>
  );
}
